import { IdentityServiceError, NETWORK_MESSAGE, errorFor } from "../live/errors";
import type { Fetch, Http } from "../live/http";
import {
  ignoreBody,
  parseAi,
  parseBulk,
  parseDeleted,
  parseExport,
  parseExportLink,
  parseExports,
  parseFactorReset,
  parseGrants,
  parseInbox,
  parseInformation,
  parseRules,
  parseSettings,
  parseSignOut,
  parseTeam,
  parseTeams,
  parseVerdict,
} from "./schemas";
import type { ConsoleApi, Download } from "./types";

const enc = encodeURIComponent;
const org = (id: string) => `/orgs/${enc(id)}`;
const project = (id: string) => `/projects/${enc(id)}`;

/** The phase 2 routes of the identity service (`crates/services/identity/src/routes/mod.rs`). */
export const CONSOLE_ROUTES = {
  deleted: (o: string) => `${org(o)}/projects/deleted`,
  deletedOne: (o: string, p: string) => `${org(o)}/projects/deleted/${enc(p)}`,
  restore: (o: string, p: string) => `${org(o)}/projects/deleted/${enc(p)}/restore`,
  rules: (o: string) => `${org(o)}/project-rules`,
  project,
  information: (p: string) => `${project(p)}/information`,
  inbox: "/me/notifications",
  readOne: (id: string) => `/me/notifications/${enc(id)}/read`,
  readAll: "/me/notifications/read-all",
  mySettings: "/me/notification-settings",
  projectSettings: (p: string) => `${project(p)}/notification-settings`,
  bulk: (o: string) => `${org(o)}/invitations/bulk`,
  teams: (o: string) => `${org(o)}/teams`,
  team: (o: string, t: string) => `${org(o)}/teams/${enc(t)}`,
  teamMember: (o: string, t: string, u: string) => `${org(o)}/teams/${enc(t)}/members/${enc(u)}`,
  teamProject: (p: string, t: string) => `${project(p)}/teams/${enc(t)}`,
  grants: (p: string, f: string) => `${project(p)}/folders/${enc(f)}/grants`,
  auditExport: (o: string, query: string) => `${org(o)}/audit/export?${query}`,
  auditVerify: (o: string) => `${org(o)}/audit/verify`,
  auditVerifyExport: (o: string) => `${org(o)}/audit/verify-export`,
  exports: (o: string) => `${org(o)}/exports`,
  /** On the doc service's files API, not identity's. */
  exportLink: (e: string, part: string) =>
    `/v1/organisation-exports/${enc(e)}/link?part=${encodeURIComponent(part)}`,
  ai: (o: string) => `${org(o)}/ai`,
  memberSignOut: (o: string, u: string) => `${org(o)}/members/${enc(u)}/sign-out`,
  factorReset: (o: string, u: string) => `${org(o)}/members/${enc(u)}/reset-second-factor`,
} as const;

export const SHA256_HEADER = "X-Orthos-Sha256";
export const SIGNATURE_HEADER = "X-Orthos-Signature";

/** The file name in `Content-Disposition: attachment; filename="…"`, or the fallback. */
export function attachmentName(disposition: string | null, fallback: string): string {
  const match = /filename="?([^";]+)"?/i.exec(disposition ?? "");
  return match?.[1] ?? fallback;
}

/** Calls whose request or reply is not JSON: downloads, and a JSON Lines export sent back to verify. */
function createRaw(apiPath: string, fetchImpl: Fetch) {
  const call = async (path: string, init: RequestInit): Promise<Response> => {
    let response: Response;
    try {
      response = await fetchImpl(`${apiPath}${path}`, { credentials: "same-origin", ...init });
    } catch {
      throw new IdentityServiceError("network", 0, NETWORK_MESSAGE);
    }
    if (!response.ok) {
      const text = await response.text();
      let body: unknown;
      try {
        body = text === "" ? undefined : (JSON.parse(text) as unknown);
      } catch {
        body = undefined;
      }
      throw errorFor(response.status, body);
    }
    return response;
  };
  return {
    async download(path: string, fallback: string): Promise<Download> {
      const response = await call(path, { method: "GET" });
      return {
        blob: await response.blob(),
        filename: attachmentName(response.headers.get("Content-Disposition"), fallback),
        sha256: response.headers.get(SHA256_HEADER) ?? undefined,
        signature: response.headers.get(SIGNATURE_HEADER) ?? undefined,
      };
    },
    async postText(path: string, body: string, headers: Record<string, string>): Promise<unknown> {
      const response = await call(path, {
        method: "POST",
        body,
        headers: { ...headers, "Content-Type": "text/plain", Accept: "application/json" },
      });
      return (await response.json()) as unknown;
    },
  };
}

/** The phase 2 admin and project calls against the identity service. */
export function createLiveConsoleApi(
  http: Http,
  apiPath: string,
  fetchImpl: Fetch,
  files: { http: Http; path: string },
): ConsoleApi {
  const raw = createRaw(apiPath, fetchImpl);
  const R = CONSOLE_ROUTES;
  return {
    listDeleted: (o) => http.get(R.deleted(o), parseDeleted),
    deleteProject: (p) => http.sendOnly("DELETE", R.project(p), undefined),
    restoreProject: async (o, p) => {
      await http.send("POST", R.restore(o, p), {}, ignoreBody);
    },
    deleteForever: (o, p, confirm) => http.sendOnly("DELETE", R.deletedOne(o, p), { confirm }),
    projectRules: (o) => http.get(R.rules(o), parseRules),
    setProjectRules: (o, rules) => http.send("PUT", R.rules(o), rules, parseRules),

    information: (p) => http.get(R.information(p), parseInformation),
    saveInformation: (p, input) => {
      const body = Object.fromEntries(
        Object.entries(input).map(([key, value]) => [
          key,
          value.trim() === "" ? null : value.trim(),
        ]),
      );
      return http.send("PUT", R.information(p), body, parseInformation);
    },

    inbox: () => http.get(R.inbox, parseInbox),
    readNotification: (id) => http.sendOnly("POST", R.readOne(id), undefined),
    readAllNotifications: () => http.sendOnly("POST", R.readAll, undefined),
    notificationSettings: (p) =>
      http.get(p === undefined ? R.mySettings : R.projectSettings(p), parseSettings),
    saveNotificationSettings: (settings, p) =>
      http.send(
        "PUT",
        p === undefined ? R.mySettings : R.projectSettings(p),
        { settings },
        parseSettings,
      ),
    clearProjectNotificationSettings: (p) =>
      http.sendOnly("DELETE", R.projectSettings(p), undefined),

    bulkInvite: (o, input) => http.send("POST", R.bulk(o), input, parseBulk),

    teams: (o) => http.get(R.teams(o), parseTeams),
    createTeam: (o, name, handle) =>
      http.send("POST", R.teams(o), handle === undefined ? { name } : { name, handle }, parseTeam),
    renameTeam: (o, t, name) => http.send("PATCH", R.team(o, t), { name }, parseTeam),
    deleteTeam: (o, t) => http.sendOnly("DELETE", R.team(o, t), undefined),
    setTeamMember: (o, t, u, teamAdmin) =>
      http.send("PUT", R.teamMember(o, t, u), { teamAdmin }, parseTeam),
    removeTeamMember: (o, t, u) => http.sendOnly("DELETE", R.teamMember(o, t, u), undefined),
    setTeamProjectRole: (p, t, role) => http.send("PUT", R.teamProject(p, t), { role }, parseTeam),
    removeTeamProjectRole: (p, t) => http.sendOnly("DELETE", R.teamProject(p, t), undefined),

    folderGrants: (p, f) => http.get(R.grants(p, f), parseGrants),
    setFolderGrants: (p, f, grants) => http.send("PUT", R.grants(p, f), { grants }, parseGrants),

    exportAudit: (o, format, query) => {
      const params = new URLSearchParams(query);
      params.set("format", format);
      return raw.download(R.auditExport(o, params.toString()), `audit-log.${format}`);
    },
    verifyAudit: (o) => http.get(R.auditVerify(o), parseVerdict),
    verifyAuditExport: async (o, body, signature) =>
      parseVerdict.parse(
        await raw.postText(R.auditVerifyExport(o), body, { [SIGNATURE_HEADER]: signature }),
      ),

    exports: (o) => http.get(R.exports(o), parseExports),
    startExport: (o, projects) => http.send("POST", R.exports(o), { projects }, parseExport),
    exportLink: async (e, part) => {
      const link = await files.http.send("POST", R.exportLink(e, part), {}, parseExportLink);
      // A development link is a path on the files API (`/files/v1/…`), which the app reaches under its own path.
      const url = link.url.startsWith("/files/")
        ? `${files.path}${link.url.slice("/files".length)}`
        : link.url;
      return { ...link, url };
    },

    aiEnabled: (o) => http.get(R.ai(o), parseAi),
    setAiEnabled: (o, enabled) => http.send("PUT", R.ai(o), { enabled }, parseAi),
    signOutMember: (o, u) => http.send("POST", R.memberSignOut(o, u), {}, parseSignOut),
    resetSecondFactor: (o, u) => http.send("POST", R.factorReset(o, u), {}, parseFactorReset),
  };
}
